import { apiUrl } from "../config/env";

/**
 * Where sign-in starts, and how the app learns that one failed.
 *
 * Canon decision `D-008` makes Google the only way a founder is known, and
 * `contracts/API.md` § Seller sign-in shape makes both legs **top-level navigations**
 * rather than `fetch` calls. That is the load-bearing property this module protects: the
 * app hands the browser an address and steps out of the way, so it never holds, parses or
 * forwards a credential of its own. There is nothing here to exchange, nothing to store,
 * and nothing that could be pasted into it.
 *
 * The management link this replaced had to be read out of the address bar and scrubbed
 * from it. Its removal is why this file is as small as it is.
 */

/** The invite code canon § 5 attaches a signup source to, as it arrives on the app's URL. */
export const INVITE_PARAM = "invite";

/** What core adds to the return address when a sign-in did not complete. */
export const SIGN_IN_ERROR_PARAM = "error";

/** The one code every sign-in refusal carries — core merges the causes deliberately. */
export const SIGN_IN_FAILED = "SIGN_IN_FAILED";

/**
 * The address of `GET /v1/seller/session/start`, carrying the invite code when the founder
 * arrived with one.
 *
 * The code is **forwarded, not judged**. Core resolves it against configuration at the
 * start of the round trip and stores only a code that matched, so a value this app decided
 * to like could not make a signup an `invite` one anyway — and a length or shape rule kept
 * here would be a second opinion drifting away from the one that counts (`contracts/API.md`
 * § Seller sign-in shape).
 *
 * It is not scrubbed from the app's own address bar either. It is not a secret, and a
 * founder who reloads this page before signing in must keep the larger canon § 5 grant
 * rather than lose it to tidiness. It stops being readable where it matters: core's return
 * address carries nothing at all.
 */
export function signInUrl(search: string): string {
  const presented = new URLSearchParams(search).get(INVITE_PARAM);
  const url = new URL(apiUrl("/v1/seller/session/start"));
  if (presented !== null && presented !== "") {
    url.searchParams.set(INVITE_PARAM, presented);
  }
  return url.toString();
}

/**
 * Did the founder come back from a refused sign-in?
 *
 * One question with one answer, because core answers every refusal — an unknown state, a
 * replayed one, a code Google rejected, a forged ID token, an unverified address, a revoked
 * binding, a disabled seller, an unconfigured environment — with the identical redirect.
 * The app cannot tell those apart and must not appear to.
 */
export function signInWasRefused(search: string): boolean {
  return new URLSearchParams(search).get(SIGN_IN_ERROR_PARAM) === SIGN_IN_FAILED;
}
